import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { fetchEventsForDay, isSlotBooked, getCalendarIdForCourt } from '@/lib/gcal';
import { buildFromToISO } from '@/lib/timeUtils';
import { type CourtKey, CANONICAL_KEYS } from '@/lib/courts';
import { ChevronLeft, ChevronRight, CalendarDays, CheckCircle2, Clock } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────

interface CourtMeta {
  key: CourtKey;
  label: string;
  shortLabel: string;
  sport: 'padel' | 'tennis';
  courtNumber: string;
}

interface SelectedSlot {
  courtKey: CourtKey;
  time: string;
  date: Date;
}

interface CourtOverviewProps {
  onSelectSlot: (courtKey: CourtKey, time: string, date: Date) => void;
  bookingFormRef: React.RefObject<HTMLDivElement>;
}

// ── Constants ────────────────────────────────────────────────────────────────

const COURTS: CourtMeta[] = [
  { key: 'padel-1',  label: 'Padel Platz 1',  shortLabel: 'P1', sport: 'padel',  courtNumber: '1' },
  { key: 'padel-2',  label: 'Padel Platz 2',  shortLabel: 'P2', sport: 'padel',  courtNumber: '2' },
  { key: 'tennis-1', label: 'Tennis Platz 1', shortLabel: 'T1', sport: 'tennis', courtNumber: '1' },
  { key: 'tennis-2', label: 'Tennis Platz 2', shortLabel: 'T2', sport: 'tennis', courtNumber: '2' },
  { key: 'tennis-3', label: 'Tennis Platz 3', shortLabel: 'T3', sport: 'tennis', courtNumber: '3' },
];

// 08:00 → 23:00, 30-min slots (last start = 22:30)
const TIME_SLOTS: string[] = Array.from({ length: 30 }, (_, i) => {
  const totalMinutes = i * 30;
  const hour = Math.floor(totalMinutes / 60) + 8;
  const minute = totalMinutes % 60;
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
});

const EVENING_START = '18:00';

function formatDateISO(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function formatDateLabel(d: Date): string {
  const today = new Date();
  const tomorrow = addDays(today, 1);
  if (isSameDay(d, today)) return 'Heute';
  if (isSameDay(d, tomorrow)) return 'Morgen';
  const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  return `${days[d.getDay()]}, ${d.getDate()}. ${months[d.getMonth()]}`;
}


function getPastCutoff(): string | null {
  const now = new Date();
  const h = now.getHours();
  const m = now.getMinutes();
  const totalMin = h * 60 + m;
  if (totalMin < 8 * 60) return null;
  const blocked = Math.floor(totalMin / 30) * 30;
  const bh = Math.floor(blocked / 60);
  const bm = blocked % 60;
  return `${bh.toString().padStart(2, '0')}:${bm.toString().padStart(2, '0')}`;
}

// ── Main Component ────────────────────────────────────────────────────────────

const CourtOverview: React.FC<CourtOverviewProps> = ({ onSelectSlot, bookingFormRef }) => {
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const maxDate = useMemo(() => addDays(today, 7), [today]);

  const [overviewDate, setOverviewDate] = useState<Date>(today);
  const [filter, setFilter] = useState<'all' | 'padel' | 'tennis' | 'evening' | 'min1h' | 'min2h'>('all');
  const [bookedMap, setBookedMap] = useState<Record<CourtKey, Set<string>>>({} as Record<CourtKey, Set<string>>);
  const [loading, setLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<SelectedSlot | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const selectedCardRef = useRef<HTMLDivElement>(null);

  // Close date picker on outside click
  useEffect(() => {
    if (!showDatePicker) return;
    const handler = (e: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDatePicker]);

  // Fetch all 5 courts for the selected date
  const fetchAll = useCallback(async (d: Date) => {
    setLoading(true);
    const dateStr = formatDateISO(d);
    const results = {} as Record<CourtKey, Set<string>>;

    await Promise.all(
      CANONICAL_KEYS.map(async (courtKey) => {
        const calId = getCalendarIdForCourt(courtKey);
        if (!calId) { results[courtKey] = new Set(); return; }
        try {
          const events = await fetchEventsForDay(calId, dateStr);
          const booked = new Set<string>();
          TIME_SLOTS.forEach((slot) => {
            const startLocal = `${dateStr}T${slot}`;
            try {
              const { from, to } = buildFromToISO(startLocal, 30);
              if (isSlotBooked(from, to, events)) booked.add(slot);
            } catch { /* ignore parse errors */ }
          });
          results[courtKey] = booked;
        } catch {
          results[courtKey] = new Set();
        }
      })
    );

    setBookedMap(results);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll(overviewDate);
    setSelectedSlot(null);
  }, [overviewDate, fetchAll]);

  // Past cutoff (only relevant for today)
  const pastCutoff = useMemo(() => {
    if (isSameDay(overviewDate, today)) return getPastCutoff();
    return null;
  }, [overviewDate, today]);

  const isSlotPast = (slot: string): boolean => {
    if (!pastCutoff) return false;
    return slot < pastCutoff;
  };

  const isSlotFree = (courtKey: CourtKey, slot: string): boolean => {
    if (!bookedMap[courtKey]) return true;
    return !bookedMap[courtKey].has(slot);
  };

  // Filter: check if a court passes the current filter
  const courtPassesFilter = useCallback((courtKey: CourtKey): boolean => {
    const meta = COURTS.find(c => c.key === courtKey)!;
    if (filter === 'padel') return meta.sport === 'padel';
    if (filter === 'tennis') return meta.sport === 'tennis';
    return true;
  }, [filter]);

  // Filter: check if a slot passes time/duration filters
  const slotPassesFilter = useCallback((courtKey: CourtKey, slot: string): boolean => {
    if (filter === 'evening') return slot >= EVENING_START;
    if (filter === 'min1h') {
      // need 2 consecutive free slots starting here
      const idx = TIME_SLOTS.indexOf(slot);
      if (idx < 0 || idx + 1 >= TIME_SLOTS.length) return false;
      return isSlotFree(courtKey, slot) && isSlotFree(courtKey, TIME_SLOTS[idx + 1]);
    }
    if (filter === 'min2h') {
      const idx = TIME_SLOTS.indexOf(slot);
      if (idx < 0 || idx + 3 >= TIME_SLOTS.length) return false;
      return [0,1,2,3].every(o => isSlotFree(courtKey, TIME_SLOTS[idx + o]));
    }
    return true;
  }, [filter, bookedMap, overviewDate]);

  // Visible courts based on filter
  const visibleCourts = useMemo(() =>
    COURTS.filter(c => courtPassesFilter(c.key)),
    [courtPassesFilter]
  );

  // Visible time slots based on filter
  const visibleSlots = useMemo(() => {
    if (filter === 'evening') return TIME_SLOTS.filter(s => s >= EVENING_START);
    return TIME_SLOTS;
  }, [filter]);

  const handleSlotClick = (courtKey: CourtKey, time: string) => {
    setSelectedSlot({ courtKey, time, date: overviewDate });
    onSelectSlot(courtKey, time, overviewDate);
    setTimeout(() => {
      bookingFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const handleDateChange = (d: Date) => {
    if (d < today || d > maxDate) return;
    setOverviewDate(d);
    setShowDatePicker(false);
  };

  const goToPrev = () => {
    const prev = addDays(overviewDate, -1);
    if (prev >= today) setOverviewDate(prev);
  };
  const goToNext = () => {
    const next = addDays(overviewDate, 1);
    if (next <= maxDate) setOverviewDate(next);
  };

  // Count free slots per court (for stat display)
  const freeCountPerCourt = useMemo(() => {
    const result = {} as Record<CourtKey, number>;
    CANONICAL_KEYS.forEach(k => {
      result[k] = TIME_SLOTS.filter(s => !isSlotPast(s) && isSlotFree(k, s)).length;
    });
    return result;
  }, [bookedMap, pastCutoff]);

  // Mini date picker calendar
  const renderMiniCalendar = () => {
    const firstDay = new Date(overviewDate.getFullYear(), overviewDate.getMonth(), 1);
    const startDow = firstDay.getDay(); // 0=Sun
    const daysInMonth = new Date(overviewDate.getFullYear(), overviewDate.getMonth() + 1, 0).getDate();
    const monthNames = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
    const cells: (Date | null)[] = [
      ...Array(startDow).fill(null),
      ...Array.from({ length: daysInMonth }, (_, i) => new Date(overviewDate.getFullYear(), overviewDate.getMonth(), i + 1))
    ];
    while (cells.length % 7 !== 0) cells.push(null);

    return (
      <div ref={datePickerRef} className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.18)] border border-gray-100 p-4 w-72">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => { const d = new Date(overviewDate); d.setMonth(d.getMonth()-1); if(d >= today) setOverviewDate(d); else setOverviewDate(today); }} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>
          <span className="text-[12px] font-700 tracking-widest uppercase text-gray-800" style={{fontWeight:700}}>
            {monthNames[overviewDate.getMonth()]} {overviewDate.getFullYear()}
          </span>
          <button onClick={() => { const d = new Date(overviewDate); d.setMonth(d.getMonth()+1); if(d <= maxDate) setOverviewDate(d); }} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </div>
        <div className="grid grid-cols-7 mb-1">
          {['So','Mo','Di','Mi','Do','Fr','Sa'].map(d => (
            <div key={d} className="text-center text-[9px] font-bold tracking-widest uppercase text-gray-400 py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-y-1">
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const isDisabled = d < today || d > maxDate;
            const isSelected = isSameDay(d, overviewDate);
            const isToday = isSameDay(d, new Date());
            return (
              <button
                key={i}
                disabled={isDisabled}
                onClick={() => handleDateChange(d)}
                className={`mx-auto w-8 h-8 flex items-center justify-center rounded-full text-[12px] font-medium transition-all
                  ${isDisabled ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer hover:bg-red-50 hover:text-red-600'}
                  ${isSelected ? 'bg-red-600 text-white font-bold shadow-[0_2px_8px_rgba(220,38,38,0.4)] scale-110' : ''}
                  ${isToday && !isSelected ? 'ring-1 ring-red-500 text-red-600 font-bold' : ''}
                `}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Slot status helper ────────────────────────────────────────────────────
  type SlotStatus = 'free' | 'booked' | 'past' | 'selected';

  const getSlotStatus = (courtKey: CourtKey, slot: string): SlotStatus => {
    if (selectedSlot?.courtKey === courtKey && selectedSlot.time === slot && isSameDay(selectedSlot.date, overviewDate)) return 'selected';
    if (isSlotPast(slot)) return 'past';
    if (!isSlotFree(courtKey, slot)) return 'booked';
    return 'free';
  };

  const FILTER_OPTIONS = [
    { id: 'all',     label: 'Alle Plätze' },
    { id: 'padel',   label: 'Padel' },
    { id: 'tennis',  label: 'Tennis' },
    { id: 'evening', label: 'Abendzeiten' },
    { id: 'min1h',   label: 'Mind. 1h frei' },
    { id: 'min2h',   label: 'Mind. 2h frei' },
  ] as const;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <section className="mb-10">
      {/* ── Header ── */}
      <div className="mb-6">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 rounded-full border border-emerald-200">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[10px] font-bold tracking-[0.12em] uppercase text-emerald-700">Live verfügbar</span>
          </div>
        </div>
        <h2 className="text-[22px] sm:text-[26px] font-bold text-gray-900 tracking-tight leading-tight">
          Alle Plätze auf einen Blick
        </h2>
        <p className="text-[13px] text-gray-500 mt-1 font-light">
          Wählen Sie ein Datum und sehen Sie sofort, welche Plätze verfügbar sind.
        </p>
      </div>

      {/* ── Main Card ── */}
      <div className="bg-white rounded-[20px] shadow-[0_2px_20px_rgba(0,0,0,0.06)] border border-gray-100 overflow-hidden">

        {/* ── Date Controls Bar ── */}
        <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-gray-100 px-5 sm:px-7 py-4">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Today / Tomorrow quick buttons */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => handleDateChange(today)}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-all duration-150
                  ${isSameDay(overviewDate, today)
                    ? 'bg-gray-900 text-white shadow-[0_2px_8px_rgba(0,0,0,0.2)]'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                Heute
              </button>
              <button
                onClick={() => handleDateChange(addDays(today, 1))}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-all duration-150
                  ${isSameDay(overviewDate, addDays(today, 1))
                    ? 'bg-gray-900 text-white shadow-[0_2px_8px_rgba(0,0,0,0.2)]'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                Morgen
              </button>
            </div>

            {/* Prev / Date / Next */}
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <button
                onClick={goToPrev}
                disabled={isSameDay(overviewDate, today)}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>

              <div className="relative flex-1 flex justify-center">
                <button
                  onClick={() => setShowDatePicker(v => !v)}
                  className="flex items-center gap-2 px-4 py-1.5 rounded-full border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-all group"
                >
                  <CalendarDays className="w-3.5 h-3.5 text-gray-400 group-hover:text-red-500 transition-colors" />
                  <span className="text-[13px] font-semibold text-gray-800 whitespace-nowrap">{formatDateLabel(overviewDate)}</span>
                </button>
                {showDatePicker && renderMiniCalendar()}
              </div>

              <button
                onClick={goToNext}
                disabled={overviewDate >= maxDate}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* Refresh indicator */}
            {loading && (
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin" />
                <span className="text-[11px] text-gray-400 hidden sm:block">Lädt…</span>
              </div>
            )}
          </div>

          {/* Filter chips */}
          <div className="flex items-center gap-1.5 mt-3 overflow-x-auto no-scrollbar pb-0.5">
            {FILTER_OPTIONS.map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`flex-shrink-0 px-3 py-1 rounded-full text-[11px] font-semibold transition-all duration-150
                  ${filter === f.id
                    ? 'bg-red-600 text-white shadow-[0_2px_8px_rgba(220,38,38,0.3)]'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Selected Slot Summary Card ── */}
        {selectedSlot && isSameDay(selectedSlot.date, overviewDate) && (
          <div ref={selectedCardRef} className="mx-5 sm:mx-7 mt-5 p-4 bg-gradient-to-r from-red-600 to-red-700 rounded-[14px] flex items-center justify-between gap-3 shadow-[0_4px_20px_rgba(220,38,38,0.3)]">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-red-200 mb-0.5">Ausgewählt</div>
                <div className="text-[13px] font-semibold text-white truncate">
                  {COURTS.find(c => c.key === selectedSlot.courtKey)?.label} · {formatDateLabel(selectedSlot.date)} · {selectedSlot.time} Uhr
                </div>
              </div>
            </div>
            <button
              onClick={() => bookingFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="flex-shrink-0 px-4 py-2 bg-white text-red-700 rounded-full text-[11px] font-bold hover:bg-red-50 transition-colors shadow-sm whitespace-nowrap"
            >
              Buchung unten abschließen
            </button>
          </div>
        )}

        {/* ── DESKTOP: Schedule Grid ── */}
        <div className="hidden md:block overflow-x-auto px-5 sm:px-7 py-5">
          <div className="min-w-[640px]">
            {/* Column headers */}
            <div className="grid gap-1 mb-2" style={{ gridTemplateColumns: '52px repeat(5, 1fr)' }}>
              <div /> {/* time col */}
              {visibleCourts.map(court => (
                <div key={court.key} className="text-center px-2 py-2">
                  <div className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-[0.08em] mb-1
                    ${court.sport === 'padel' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
                    {court.sport === 'padel' ? 'Padel' : 'Tennis'}
                  </div>
                  <div className="text-[12px] font-bold text-gray-800 leading-none">{court.label}</div>
                  {!loading && (
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      <span className={freeCountPerCourt[court.key] > 0 ? 'text-emerald-600 font-semibold' : 'text-gray-400'}>
                        {freeCountPerCourt[court.key]} frei
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Slot rows */}
            <div className="space-y-0.5">
              {visibleSlots.map((slot, rowIdx) => {
                const isHour = slot.endsWith(':00');
                const isEveningStart = slot === EVENING_START;
                return (
                  <React.Fragment key={slot}>
                    {isEveningStart && (
                      <div className="grid gap-1 py-1" style={{ gridTemplateColumns: '52px repeat(5, 1fr)' }}>
                        <div />
                        <div className="col-span-5 flex items-center gap-2">
                          <div className="flex-1 h-px bg-amber-200" />
                          <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-amber-500 px-2">Abend</span>
                          <div className="flex-1 h-px bg-amber-200" />
                        </div>
                      </div>
                    )}
                    <div
                      className={`grid gap-1 rounded-lg transition-colors ${isHour && rowIdx > 0 ? 'mt-1' : ''}`}
                      style={{ gridTemplateColumns: '52px repeat(5, 1fr)' }}
                    >
                      {/* Time label */}
                      <div className={`flex items-center justify-end pr-3 ${isHour ? 'text-[12px] font-bold text-gray-700' : 'text-[10px] text-gray-400'}`}>
                        {isHour ? slot : <span className="opacity-60">{slot}</span>}
                      </div>

                      {/* Court slots */}
                      {visibleCourts.map(court => {
                        const status = getSlotStatus(court.key, slot);
                        const passesFilter = filter === 'min1h' || filter === 'min2h'
                          ? slotPassesFilter(court.key, slot)
                          : true;

                        if ((filter === 'min1h' || filter === 'min2h') && !passesFilter && status !== 'selected') {
                          return (
                            <div key={court.key} className="h-8 rounded-lg bg-gray-50/50 border border-dashed border-gray-100" />
                          );
                        }

                        return (
                          <button
                            key={court.key}
                            disabled={status !== 'free'}
                            onClick={() => status === 'free' && handleSlotClick(court.key, slot)}
                            className={`h-8 rounded-lg text-[10px] font-semibold transition-all duration-150 border relative overflow-hidden
                              ${status === 'free'
                                ? 'bg-white border-gray-200 text-gray-700 hover:border-red-400 hover:bg-red-50 hover:text-red-700 hover:shadow-[0_2px_8px_rgba(220,38,38,0.15)] cursor-pointer active:scale-[0.97]'
                                : status === 'selected'
                                ? 'bg-gradient-to-br from-red-500 to-red-700 border-red-600 text-white shadow-[0_2px_12px_rgba(220,38,38,0.4)] scale-[1.03] cursor-default'
                                : status === 'booked'
                                ? 'bg-red-50 border-red-200 text-red-300 cursor-not-allowed'
                                : 'bg-gray-200 border-gray-300 text-gray-400 cursor-not-allowed'
                              }
                            `}
                          >
                            {status === 'free' && <span className="hidden group-hover:block">+</span>}
                            {status === 'selected' && <CheckCircle2 className="w-3 h-3 mx-auto" />}
                            {status === 'booked' && <div className="w-full h-full flex items-center justify-center"><div className="w-8 h-px bg-red-300 rotate-12" /></div>}
                          </button>
                        );
                      })}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── MOBILE: Compact grid — courts as columns, hours as rows ── */}
        <div className="md:hidden py-4 px-3">
          {loading ? (
            <div className="flex flex-col items-center gap-3 py-10 text-gray-400">
              <div className="w-8 h-8 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin" />
              <span className="text-[13px]">Verfügbarkeit wird geladen…</span>
            </div>
          ) : (
            <div>
              {/* Column header row */}
              <div className="flex items-center gap-1 mb-2 pl-12">
                {visibleCourts.map(court => (
                  <div key={court.key} className="flex-1 text-center">
                    <div className={`text-[9px] font-bold uppercase tracking-wide ${court.sport === 'padel' ? 'text-blue-500' : 'text-amber-500'}`}>
                      {court.sport === 'padel' ? 'Pad' : 'Ten'}
                    </div>
                    <div className="text-[11px] font-bold text-gray-700">{court.courtNumber}</div>
                  </div>
                ))}
              </div>

              {/* Rows: one per hour, each with :00 and :30 sub-rows */}
              <div className="space-y-px">
                {visibleSlots
                  .filter(s => s.endsWith(':00'))
                  .map(hourSlot => {
                    const halfSlot = hourSlot.replace(':00', ':30');
                    const subSlots = [hourSlot, ...(visibleSlots.includes(halfSlot) ? [halfSlot] : [])];
                    const isEveningStart = hourSlot === EVENING_START;

                    return (
                      <React.Fragment key={hourSlot}>
                        {isEveningStart && (
                          <div className="flex items-center gap-2 py-2">
                            <div className="flex-1 h-px bg-amber-200" />
                            <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-amber-500">Abend</span>
                            <div className="flex-1 h-px bg-amber-200" />
                          </div>
                        )}

                        {subSlots.map((slot, si) => {
                          const isHourRow = slot.endsWith(':00');
                          return (
                            <div key={slot} className={`flex items-center gap-1 ${isHourRow && si === 0 ? 'pt-1' : ''}`}>
                              {/* Time label */}
                              <div className="w-11 flex-shrink-0 text-right pr-2">
                                {isHourRow
                                  ? <span className="text-[12px] font-bold text-gray-700 tabular-nums">{slot}</span>
                                  : <span className="text-[10px] text-gray-400 tabular-nums">{slot}</span>
                                }
                              </div>

                              {/* Court cells */}
                              {visibleCourts.map(court => {
                                const status = getSlotStatus(court.key, slot);
                                return (
                                  <button
                                    key={court.key}
                                    disabled={status !== 'free'}
                                    onClick={() => status === 'free' && handleSlotClick(court.key, slot)}
                                    className={`flex-1 h-8 rounded-md text-[10px] font-bold transition-all duration-150 flex items-center justify-center
                                      ${status === 'free'
                                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 active:scale-95 cursor-pointer border border-emerald-200'
                                        : status === 'selected'
                                        ? 'bg-red-600 text-white border border-red-600 shadow-sm'
                                        : status === 'booked'
                                        ? 'bg-red-50 border border-red-200 cursor-not-allowed'
                                        : 'bg-gray-100 border border-gray-200 cursor-not-allowed'
                                      }`}
                                  >
                                    {status === 'free' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                                    {status === 'selected' && <CheckCircle2 className="w-3 h-3" />}
                                    {status === 'booked' && <span className="w-1.5 h-1.5 rounded-full bg-red-300" />}
                                    {status === 'past' && <span className="w-3 h-px bg-gray-400 rotate-12 inline-block" />}
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
              </div>

              {/* Mobile legend */}
              <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100 flex-wrap">
                {[
                  { bg: 'bg-emerald-100 border-emerald-200', dot: 'bg-emerald-500', label: 'Frei' },
                  { bg: 'bg-red-600 border-red-600', dot: null, icon: true, label: 'Gewählt' },
                  { bg: 'bg-red-50 border-red-200', dot: 'bg-red-300', label: 'Belegt' },
                  { bg: 'bg-gray-100 border-gray-200', dot: null, label: 'Vergangen' },
                ].map(({ bg, dot, label, icon }) => (
                  <div key={label} className="flex items-center gap-1.5">
                    <div className={`w-5 h-5 rounded flex items-center justify-center border ${bg}`}>
                      {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}
                      {icon && <CheckCircle2 className="w-2.5 h-2.5 text-white" />}
                    </div>
                    <span className="text-[10px] text-gray-500 font-medium">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Legend ── */}
        <div className="px-5 sm:px-7 py-4 border-t border-gray-100 flex items-center gap-5 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-400">Legende</span>
          {[
            { color: 'bg-white border-gray-300', label: 'Frei' },
            { color: 'bg-red-600 border-red-600', label: 'Gewählt' },
            { color: 'bg-red-50 border-red-200', label: 'Belegt' },
            { color: 'bg-gray-200 border-gray-300', label: 'Vergangen' },
          ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded-sm border ${color}`} />
              <span className="text-[11px] text-gray-500">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Classic booking link ── */}
      <div className="flex items-center justify-center mt-4">
        <button
          onClick={() => bookingFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          className="flex items-center gap-1.5 text-[12px] text-gray-400 hover:text-gray-600 transition-colors"
        >
          <Clock className="w-3.5 h-3.5" />
          <span>Klassische Buchung verwenden</span>
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </section>
  );
};

export default CourtOverview;
